import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, Trophy, ChevronRight, Star, Award, Shield, Heart, Medal } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import Navigation from './Navigation';
import Footer from './Footer';
import useCountUp from './home/useCountUp';
import CountdownTimer from './home/CountdownTimer';
import AchievementCard from './home/AchievementCard';
import OffSeasonBanner from './home/OffSeasonBanner';
import { teamInfo, newsUpdates, testimonials } from '../mock';

// Registration deadline (Aug 15th, 2026)
const REGISTRATION_DEADLINE = '2026-08-15T23:59:59';

const ACHIEVEMENTS = [
  { title: 'Division Champions 🏆', year: '2025', icon: Trophy, description: '8U Tackle Football' },
  { title: 'Super Bowl Runner-Up', year: '2025', icon: Medal, description: '8U Tackle Football' },
  { title: 'Division Champions', year: '2024', icon: Trophy, description: '10U Tackle Football' },
];

const HomePage = () => {
  const yearsCounter = useCountUp(45, 2000);
  const athletesCounter = useCountUp(500, 2500);
  const championsCounter = useCountUp(12, 1500);

  return (
    <div className="page-container">
      <Navigation />

      <main className="main-content">
        {/* Hero Section */}
        <section className="hero-section" data-testid="hero-section">
          <div className="hero-background">
            <div className="hero-overlay"></div>
          </div>
          <div className="container hero-content">
            <div className="hero-title-wrapper" data-testid="hero-title">
              <h2 className="hero-subtitle">Randallstown Panthers</h2>
              <h1 className="hero-title">Youth Football & Cheer</h1>
            </div>
            <p className="hero-tagline" data-testid="hero-tagline">{teamInfo.tagline}</p>

            {/* Animated Stats */}
            <div className="hero-stats animated-stats" data-testid="hero-stats">
              <div className="hero-stat" ref={yearsCounter.ref}>
                <span className="stat-number">{yearsCounter.count}+</span>
                <span className="stat-label">Years</span>
              </div>
              <div className="hero-stat" ref={athletesCounter.ref}>
                <span className="stat-number">{athletesCounter.count}+</span>
                <span className="stat-label">Athletes Trained</span>
              </div>
              <div className="hero-stat" ref={championsCounter.ref}>
                <span className="stat-number">{championsCounter.count}</span>
                <span className="stat-label">MMYFCL Championships</span>
              </div>
            </div>

            <CountdownTimer
              targetDate={REGISTRATION_DEADLINE}
              label="Registration Deadline - August 15th"
            />

            <div className="hero-buttons">
              <Link to="/registration">
                <Button className="btn-primary btn-large" data-testid="register-btn">Register Now</Button>
              </Link>
              <Link to="/programs">
                <Button className="btn-secondary btn-large" data-testid="programs-btn">View Programs</Button>
              </Link>
            </div>
          </div>
        </section>

        <OffSeasonBanner />

        {/* Values Preview */}
        <section className="values-preview-section">
          <div className="container">
            <div className="values-preview-grid">
              <div className="value-preview-item" data-testid="value-safety">
                <div className="value-icon-wrapper"><Shield size={32} /></div>
                <h3>Safety First</h3>
                <p>Age-appropriate training with professional supervision</p>
              </div>
              <div className="value-preview-item" data-testid="value-teamwork">
                <div className="value-icon-wrapper"><Users size={32} /></div>
                <h3>Teamwork</h3>
                <p>Building bonds that last a lifetime</p>
              </div>
              <div className="value-preview-item" data-testid="value-excellence">
                <div className="value-icon-wrapper"><Award size={32} /></div>
                <h3>Excellence</h3>
                <p>Striving to be champions on and off the field</p>
              </div>
              <div className="value-preview-item" data-testid="value-community">
                <div className="value-icon-wrapper"><Heart size={32} /></div>
                <h3>Community</h3>
                <p>Serving Randallstown families since 1979</p>
              </div>
            </div>
          </div>
        </section>

        {/* Achievement Showcase */}
        <section className="achievements-section" data-testid="achievements-section">
          <div className="container">
            <h2 className="section-title">Our Achievements</h2>
            <p className="section-subtitle">A legacy of excellence in youth sports</p>

            {/* Championship Banner */}
            <div
              className="championship-banner"
              style={{
                background: 'linear-gradient(135deg, rgba(255,215,0,0.15) 0%, rgba(0,0,0,0.8) 100%)',
                border: '2px solid var(--panthers-gold)',
                borderRadius: '16px',
                padding: '24px 32px',
                marginBottom: '32px',
                textAlign: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <Trophy size={48} style={{ color: 'var(--panthers-gold)' }} />
                <div>
                  <h3 style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--panthers-gold)', margin: 0 }}>12 Championships</h3>
                  <p style={{ color: 'rgba(255,255,255,0.8)', margin: '4px 0 0', fontSize: '1.1rem' }}>
                    Won during our time in the Mid Maryland Youth Football & Cheer League (MMYFCL)
                  </p>
                </div>
                <Trophy size={48} style={{ color: 'var(--panthers-gold)' }} />
              </div>
            </div>

            <h3 style={{ color: 'var(--panthers-gold)', marginBottom: '20px', fontSize: '1.2rem' }}>Recent Accomplishments</h3>
            <div className="achievements-grid">
              {ACHIEVEMENTS.map((achievement) => (
                <AchievementCard key={`${achievement.year}-${achievement.title}`} {...achievement} />
              ))}
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="quick-links-section" data-testid="quick-links-section">
          <div className="container">
            <h2 className="section-title">Get Started</h2>
            <p className="section-subtitle">Everything you need to join the Panthers family</p>
            <div className="quick-links-grid">
              <Link to="/programs" className="quick-link-card" data-testid="quick-link-programs">
                <Users size={40} />
                <h3>Programs</h3>
                <p>Football & Cheer for ages 5-14</p>
                <ChevronRight size={24} />
              </Link>
              <Link to="/registration" className="quick-link-card" data-testid="quick-link-registration">
                <Calendar size={40} />
                <h3>Registration</h3>
                <p>Sign up for the 2026 season</p>
                <ChevronRight size={24} />
              </Link>
              <Link to="/results" className="quick-link-card" data-testid="quick-link-results">
                <Trophy size={40} />
                <h3>Results</h3>
                <p>View team standings & scores</p>
                <ChevronRight size={24} />
              </Link>
            </div>
          </div>
        </section>

        {/* News Preview */}
        <section className="news-preview-section" data-testid="news-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Latest News</h2>
              <Link to="/about" className="view-all-link" data-testid="view-all-news">
                View All <ChevronRight size={18} />
              </Link>
            </div>
            <div className="news-grid">
              {newsUpdates.slice(0, 3).map((news) => (
                <Card key={news.id} className="news-card" data-testid={`news-card-${news.id}`}>
                  <CardContent>
                    <span className="news-date">{news.date}</span>
                    <h3 className="news-title">{news.title}</h3>
                    <p className="news-excerpt">{news.content.substring(0, 120)}...</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Preview */}
        <section className="testimonials-preview-section" data-testid="testimonials-section">
          <div className="container">
            <h2 className="section-title" style={{ color: 'var(--panthers-gold)' }}>What Parents Say</h2>
            <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.7)' }}>Hear from our Panthers family</p>
            <div className="testimonials-grid">
              {testimonials.slice(0, 2).map((testimonial) => (
                <Card key={testimonial.id} className="testimonial-card" data-testid={`testimonial-card-${testimonial.id}`}>
                  <CardContent>
                    <div className="testimonial-stars">
                      {Array.from({ length: testimonial.rating }, (_, i) => (
                        <Star key={`${testimonial.id}-star-${i}`} size={18} fill="#FFD700" color="#FFD700" />
                      ))}
                    </div>
                    <p className="testimonial-text">"{testimonial.text}"</p>
                    <div className="testimonial-author">
                      <span className="author-name">{testimonial.name}</span>
                      <span className="author-role">{testimonial.role}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section" data-testid="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Join the Panthers Family?</h2>
              <p>Registration is now open for the 2026 season!</p>
              <div className="cta-buttons">
                <Link to="/registration">
                  <Button className="btn-primary btn-large" data-testid="cta-register-btn">Register Today</Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-secondary btn-large" data-testid="cta-contact-btn">Contact Us</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
